import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircleIcon, Copy } from 'lucide-react';
import { ReactNode, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';

import { apiPutStoryById, APIPutStoryPayload, Story, StoryMetadata } from '@/shared/api';
import { storyByIdQueryOptions } from '@/shared/queries';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import { getShareStoryLinkMessage, INPUT_DEBOUNCE_TIME } from '@/shared/utils';

import { useLanguage } from './language-provider';

type StoryShareLinkDialogProps = {
  children: ReactNode;
  storyId: string;
  metadata: StoryMetadata;
};

export const StoryShareLinkDialog = ({
  children,
  storyId,
  metadata: propsMetadata,
}: StoryShareLinkDialogProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { language } = useLanguage();

  const [metadata, setMetadata] = useState<StoryMetadata>(propsMetadata);

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const mutation = useMutation<Story, Error, { id: string; payload: APIPutStoryPayload }>(
    {
      mutationFn: (data) => {
        return apiPutStoryById(data.id, data.payload).then((data) => data.data);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: storyByIdQueryOptions(storyId).queryKey,
        });
      },
    },
  );

  const handleChange = (key: keyof StoryMetadata, value: string) => {
    setMetadata((prev) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      const updatedMetadata = { ...prev, [key]: value };

      debounceTimeoutRef.current = setTimeout(() => {
        mutation.mutate({ id: storyId, payload: { metadata: updatedMetadata } });
      }, INPUT_DEBOUNCE_TIME);

      return updatedMetadata;
    });
  };

  const message = metadata.shareLinkMessage
    ? JSON.parse(metadata.shareLinkMessage)
    : getShareStoryLinkMessage(metadata.locale ?? language, window.location.href);

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>

      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {intl.formatMessage({ id: 'story.dialog.shareLink.title' })}
          </DialogTitle>
        </DialogHeader>

        <div className="mb-8 flex flex-col gap-2">
          <Label htmlFor="linkMessage">
            {intl.formatMessage({ id: 'story.dialog.shareLink.message' })}
          </Label>

          <Textarea
            id="linkMessage"
            autoFocus={false}
            className="min-h-[250px]"
            value={message}
            onChange={(e) =>
              handleChange('shareLinkMessage', JSON.stringify(e.target.value))
            }
          />

          <CopyShareMessageButton message={message} />
        </div>

        <div className="flex flex-col gap-2">
          <div className="grid flex-1 gap-2">
            <Label className="sr-only" htmlFor="link">
              Link
            </Label>

            <Input id="link" defaultValue={window.location.href} readOnly />
          </div>

          <CopyStoryLinkButton />
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary">
              {intl.formatMessage({ id: 'button.close' })}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CopyStoryLinkButton = () => {
  const intl = useIntl();

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          asChild
          onClick={() => {
            navigator.clipboard.writeText(window.location.href);
            toast(
              <div className="flex items-center gap-2">
                {intl.formatMessage({ id: 'linkCopied' })}{' '}
                <CheckCircleIcon color="green" size={16} />
              </div>,
              {
                duration: 2000,
              },
            );
          }}
        >
          <Button type="submit">
            <span>{intl.formatMessage({ id: 'story.dialog.shareLink.copy' })}</span>
            <Copy />
          </Button>
        </TooltipTrigger>

        <TooltipContent>
          <p>{intl.formatMessage({ id: 'tooltip.copyLink' })}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const CopyShareMessageButton = ({ message }: { message: string }) => {
  const intl = useIntl();

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          asChild
          onClick={() => {
            navigator.clipboard.writeText(message);
            toast(
              <div className="flex items-center gap-2">
                {intl.formatMessage({ id: 'story.dialog.shareLink.copyMessageSuccess' })}{' '}
                <CheckCircleIcon color="green" size={16} />
              </div>,
              {
                duration: 2000,
              },
            );
          }}
        >
          <Button type="submit">
            <span>
              {intl.formatMessage({ id: 'story.dialog.shareLink.copyMessage' })}
            </span>
            <Copy />
          </Button>
        </TooltipTrigger>

        <TooltipContent>
          <p>{intl.formatMessage({ id: 'tooltip.copyLink' })}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
